import CaretRightOutlined from '@ant-design/icons/CaretRightOutlined';
import LinkOutlined from '@ant-design/icons/LinkOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import Collapse from 'antd/es/collapse';
import Spin from 'antd/es/spin';
import Tag from 'antd/es/tag';
import theme from 'antd/es/theme';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import uniq from 'lodash.uniq';
import { useMemo, useState } from 'react';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled, { keyframes } from 'styled-components';

import { nanoidRegex } from '@mai/types';

import { useFileRevisionsQuery } from '@queries/file-revisions';
import { useSourcesQuery } from '@queries/sources';
import { useTextChunksQuery } from '@queries/text-chunks';
import { useTextContentsQuery } from '@queries/text-contents';
import { logger } from '@utils/logger';
import { useBasePath } from '@utils/router';

const pulse = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.3;
  }
`;

const StreamingDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.token.colorPrimary};
  animation: ${pulse} 1.5s ease-in-out infinite;
  margin-left: 4px;
  vertical-align: middle;
`;

// NEW styled component for streaming tag
const StyledStreamingTag = styled(Tag)`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: nowrap;
  width: fit-content;
  max-width: 100%;
`;

/* Add a new styled component for streaming text with ellipsis */
const StreamingText = styled.span`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

// Add a styled span for IEEE-style citations
const IEEECitation = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.token.colorPrimary};
  font-size: 0.8rem;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ExternalLinkIcon = styled(LinkOutlined)`
  font-size: 0.75rem;
  margin-left: 0.25rem;
  color: ${({ theme }) => theme.token.colorPrimary};
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  word-break: break-all;
  max-width: 100%;
  overflow: hidden;
`;

const UrlContainer = styled.span`
  display: inline-block;
  padding-left: 0;
  margin-left: 0;
  text-indent: 0; /* Reset text-indent */
  word-break: break-all;
  overflow-wrap: break-word;
`;

const MarkdownContainer = styled.div`
  .markdown p {
    margin: 0.25rem 0;
  }
`;

// Add styled components for tables
const StyledTable = styled.table`
  width: 100%;
  margin: 0.25rem 0;
  border-collapse: collapse;
  font-size: 0.8rem;
  border: 1px solid ${({ theme }) => theme.token.colorBorderSecondary};
  border-radius: ${({ theme }) => theme.token.borderRadiusLG}px;
  overflow: hidden;
  line-height: 1.3;
`;

const StyledTableHead = styled.thead`
  background-color: ${({ theme }) => theme.token.colorFillAlter};
`;

const StyledTableHeaderCell = styled.th`
  padding: 0.3rem 0.5rem;
  text-align: left;
  font-weight: 500;
  color: ${({ theme }) => theme.token.colorTextSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.token.colorBorderSecondary};
`;

const StyledTableBody = styled.tbody`
  background-color: ${({ theme }) => theme.token.colorBgContainer};

  tr:hover {
    background-color: ${({ theme }) => theme.token.colorFillAlter};
  }
`;

const StyledTableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.token.colorBorderSecondary};
  }
`;

const StyledTableCell = styled.td`
  padding: 0.3rem 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.token.colorBorderSecondary};
`;

/**
 * Pre process the content and find all inline citations, source_<nanoid>
 * Returns a list in order of first appearance in the content
 */
function getSourceIdsFromContent(content: string) {
  const patterns = [/\[source_(.*?)\]/g, /\[(?!chunk_)([^_]*?)\]/g];
  const matches = patterns.flatMap((pattern) =>
    Array.from(content.matchAll(pattern)).flatMap((match) => {
      const captureGroup = match[1];
      if (!captureGroup) {
        return [];
      }
      return [captureGroup];
    }),
  );
  return uniq(matches).sort((a, b) => {
    // Check for standalone format first
    const aStandaloneIndex = content.indexOf(`[${a}]`);
    const bStandaloneIndex = content.indexOf(`[${b}]`);

    // Check for source_ format
    const aSourceIndex = content.indexOf(`[source_${a}]`);
    const bSourceIndex = content.indexOf(`[source_${b}]`);

    // Determine the earliest occurrence of each ID
    const aFirstIndex =
      aStandaloneIndex !== -1 &&
      (aSourceIndex === -1 || aStandaloneIndex < aSourceIndex)
        ? aStandaloneIndex
        : aSourceIndex;

    const bFirstIndex =
      bStandaloneIndex !== -1 &&
      (bSourceIndex === -1 || bStandaloneIndex < bSourceIndex)
        ? bStandaloneIndex
        : bSourceIndex;

    // Sort by first appearance
    return aFirstIndex - bFirstIndex;
  });
}

/**
 * Pre process the content and find all inline citations, source_<nanoid>
 * Returns a list in order of first appearance in the content
 */
function getChunkIdsFromContent(content: string) {
  const patterns = [/\[chunk_(.*?)\]/g, /\[(?!source_)([^_]*?)\]/g];
  const matches = patterns.flatMap((pattern) =>
    Array.from(content.matchAll(pattern)).flatMap((match) => {
      const captureGroup = match[1];
      if (!captureGroup) {
        return [];
      }
      return [captureGroup];
    }),
  );
  return uniq(matches).sort((a, b) => {
    // Check for standalone format first
    const aStandaloneIndex = content.indexOf(`[${a}]`);
    const bStandaloneIndex = content.indexOf(`[${b}]`);

    // Check for chunk_ format
    const aChunkIndex = content.indexOf(`[chunk_${a}]`);
    const bChunkIndex = content.indexOf(`[chunk_${b}]`);

    // Determine the earliest occurrence of each ID
    const aFirstIndex =
      aStandaloneIndex !== -1 &&
      (aChunkIndex === -1 || aStandaloneIndex < aChunkIndex)
        ? aStandaloneIndex
        : aChunkIndex;

    const bFirstIndex =
      bStandaloneIndex !== -1 &&
      (bChunkIndex === -1 || bStandaloneIndex < bChunkIndex)
        ? bStandaloneIndex
        : bChunkIndex;

    // Sort by first appearance
    return aFirstIndex - bFirstIndex;
  });
}

// Add component for the bibliography section
const ReferencesSection = styled.div`
  margin-top: 1.5rem;
  font-size: 0.75rem;
`;

const ReferencesList = styled.div`
  padding-top: 0.5rem;
`;

// Update reference item styling for IEEE format
const ReferenceItem = styled.div`
  display: block;
  margin-bottom: 0.6rem;
  font-size: 0.75rem;
  line-height: 1.4;
  text-indent: -1.5rem;
  padding-left: 1.5rem;
  overflow-wrap: break-word;
`;

const ReferenceNumber = styled.span`
  font-weight: 500;
  padding-right: 0.25rem;
`;

// Custom styled Collapse for references
const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;

  .ant-collapse-header {
    padding: 0.5rem 0 !important;
    font-size: 0.85rem !important;
    font-weight: 500 !important;
    color: ${({ theme }) => theme.token.colorText} !important;
    border-top: 1px solid ${({ theme }) => theme.token.colorBorderSecondary};
  }

  .ant-collapse-content {
    background: transparent;
    border-top: none;
  }

  .ant-collapse-content-box {
    padding: 0 0 0.5rem 0 !important;
  }

  &.empty-references {
    opacity: 0.6;
    pointer-events: none;
  }
`;

const EmptyReferenceItem = styled.div`
  color: ${({ theme }) => theme.token.colorTextDisabled};
  font-size: 0.75rem;
  font-style: italic;
  margin-top: 0.5rem;
`;

// Helper function to extract domain from URL
const extractDomain = (url: string) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch (e) {
    return url;
  }
};

const MarkdownContent = ({
  content,
  isStreaming,
  streamingStatus,
  showReferences = false,
}: {
  content: string;
  isStreaming?: boolean;
  streamingStatus?: string | null;
  showReferences?: boolean;
}) => {
  const basePath = useBasePath();
  const { token } = theme.useToken();

  // Add state to track if references section is expanded
  const [referencesExpanded, setReferencesExpanded] = useState<boolean>(false);

  // Pre process the content and find all inline citations, source_<nanoid>
  const sourceIds = useMemo(() => getSourceIdsFromContent(content), [content]);

  // Pre process the content and find all inline citations, chunk_<nanoid>
  const chunkIds = useMemo(() => getChunkIdsFromContent(content), [content]);

  // Fetch text chunks
  const textChunksQuery = useTextChunksQuery(
    {
      textChunkIds: chunkIds,
      pageSize: chunkIds.length,
    },
    !!chunkIds.length,
  );

  // Log the chunk IDs and retrieved chunks for debugging
  logger.debug(`Chunk IDs extracted: ${chunkIds.join(', ')}`);
  logger.debug(`Chunks retrieved: ${textChunksQuery.data?.length || 0}`);

  // Fetch text contents
  const textContentsQuery = useTextContentsQuery(
    {
      textContentIds: textChunksQuery.data?.map((chunk) => chunk.textContentId),
      pageSize: textChunksQuery.data?.length,
    },
    !!textChunksQuery.data?.length,
  );

  // Fetch file revisions
  const fileRevisionsQuery = useFileRevisionsQuery(
    {
      fileRevisionIds: textContentsQuery.data?.map(
        (content) => content.fileRevisionId,
      ),
      pageSize: textContentsQuery.data?.length,
    },
    !!textContentsQuery.data?.length,
  );

  // Fetch source data
  const allSourceIds = uniq([
    ...sourceIds,
    ...(fileRevisionsQuery.data?.map((revision) => revision.sourceId) ?? []),
  ]).filter((id) => nanoidRegex.test(id));
  const sourcesQuery = useSourcesQuery(
    {
      sourceIds: allSourceIds,
      pageSize: allSourceIds.length,
    },
    !!allSourceIds.length,
  );

  // Map text chunks to source ids
  const textChunkIdToSourceId = textChunksQuery.data?.reduce(
    (acc, chunk) => {
      const textContent = textContentsQuery.data?.find(
        (content) => content.textContentId === chunk.textContentId,
      );
      const fileRevision = fileRevisionsQuery.data?.find(
        (revision) => revision.fileRevisionId === textContent?.fileRevisionId,
      );
      if (fileRevision?.sourceId) {
        acc[chunk.textChunkId] = fileRevision.sourceId;
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  // In our processedContent useMemo, we'll track citation numbers and sources
  const { processedContent, citationMap } = useMemo(() => {
    let contentWithLinks = content;

    // First, collect all the positions where citations appear in the text
    const citationPositions: Array<{
      sourceId: string;
      position: number;
      isChunk: boolean;
      chunkId?: string;
    }> = [];

    // Track source citations positions
    sourceIds.forEach((sourceId) => {
      // Find all standalone occurrences
      const standaloneRegex = new RegExp(`\\[${sourceId}\\](?!\\()`, 'g');
      let match;
      while ((match = standaloneRegex.exec(content)) !== null) {
        citationPositions.push({
          sourceId,
          position: match.index,
          isChunk: false,
        });
      }

      // Find all source_ occurrences
      const sourceRegex = new RegExp(`\\[source_${sourceId}\\]`, 'g');
      while ((match = sourceRegex.exec(content)) !== null) {
        citationPositions.push({
          sourceId,
          position: match.index,
          isChunk: false,
        });
      }
    });

    // Track chunk citations positions
    for (const chunkId of chunkIds) {
      const sourceId = textChunkIdToSourceId?.[chunkId];
      if (!sourceId) continue;

      // Find all standalone occurrences
      const standaloneRegex = new RegExp(`\\[${chunkId}\\](?!\\()`, 'g');
      let match;
      while ((match = standaloneRegex.exec(content)) !== null) {
        citationPositions.push({
          sourceId,
          position: match.index,
          isChunk: true,
          chunkId,
        });
      }

      // Find all chunk_ occurrences
      const chunkRegex = new RegExp(`\\[chunk_${chunkId}\\]`, 'g');
      while ((match = chunkRegex.exec(content)) !== null) {
        citationPositions.push({
          sourceId,
          position: match.index,
          isChunk: true,
          chunkId,
        });
      }
    }

    // Sort all citations by position in the text
    citationPositions.sort((a, b) => a.position - b.position);

    // Create a mapping of source IDs to their first appearance number
    const sourceIdToNumber: Record<string, number> = {};
    let nextCitationNumber = 1;

    // Assign citation numbers in order of appearance
    for (const citation of citationPositions) {
      if (!sourceIdToNumber[citation.sourceId]) {
        sourceIdToNumber[citation.sourceId] = nextCitationNumber++;
      }
    }

    // Create a mapping for text locations to citation numbers
    const textLocationToCitation: Record<string, number> = {};

    // Process source IDs
    for (const sourceId of sourceIds) {
      const url = `${basePath}/library/sources/${sourceId}`;
      const citationNumber = sourceIdToNumber[sourceId] || 0;

      // Replace all occurrences with the citation number for the source_... format
      contentWithLinks = contentWithLinks.replaceAll(
        `[source_${sourceId}]`,
        `[${citationNumber}](${url})`,
      );

      // Replace all occurrences with the citation number for the standalone id format
      // But be careful not to replace IDs inside markdown links [text](url)
      const standalonePattern = new RegExp(`\\[${sourceId}\\](?!\\()`, 'g');
      contentWithLinks = contentWithLinks.replace(
        standalonePattern,
        `[${citationNumber}](${url})`,
      );

      // Track this source citation
      textLocationToCitation[`source_${sourceId}`] = citationNumber;
    }

    // Process chunk IDs
    for (const chunkId of chunkIds) {
      const sourceId = textChunkIdToSourceId?.[chunkId];
      // Debug why chunk IDs aren't being mapped to source IDs
      logger.debug(
        `Processing chunk ID: ${chunkId}, mapped to source ID: ${sourceId || 'NONE'}`,
      );

      if (!sourceId) {
        // Even if we don't have a source ID, we still need to display the chunk citation
        // This handles chunks that don't have a corresponding source ID yet
        if (isStreaming) {
          contentWithLinks = contentWithLinks.replaceAll(
            `[chunk_${chunkId}]`,
            ``,
          );
        } else {
          contentWithLinks = contentWithLinks.replaceAll(
            `[chunk_${chunkId}]`,
            ``,
          );
        }
        continue;
      }

      const url = `${basePath}/library/sources/${sourceId}`;
      const citationNumber = sourceIdToNumber[sourceId] || 0;

      if (isStreaming) {
        // During streaming, use a placeholder but preserve the citation number
        contentWithLinks = contentWithLinks.replaceAll(
          `[chunk_${chunkId}]`,
          `[stream_${citationNumber || 'loading'}](${url})`,
        );

        // Also replace standalone IDs during streaming, but avoid markdown links
        const standalonePattern = new RegExp(`\\[${chunkId}\\](?!\\()`, 'g');
        contentWithLinks = contentWithLinks.replace(
          standalonePattern,
          `[stream_${citationNumber || 'loading'}](${url})`,
        );
      } else {
        // Use the citation number corresponding to the source
        contentWithLinks = contentWithLinks.replaceAll(
          `[chunk_${chunkId}]`,
          `[${citationNumber}](${url})`,
        );

        // Replace all occurrences with the citation number for the standalone id format
        const standalonePattern = new RegExp(`\\[${chunkId}\\](?!\\()`, 'g');
        contentWithLinks = contentWithLinks.replace(
          standalonePattern,
          `[${citationNumber}](${url})`,
        );
      }

      // Track this chunk citation
      textLocationToCitation[`chunk_${chunkId}`] = citationNumber;
    }

    // Remove adjacent duplicate links
    // Process citations in groups to identify and remove duplicates in sequences
    // First, find all citation links in the content
    const citationLinkRegex = /\[(\d+)\]\(([^)]+)\)/g;
    const allCitations = Array.from(
      contentWithLinks.matchAll(citationLinkRegex),
    );

    // Identify clusters of adjacent citations
    const citationClusters: Array<{ start: number; end: number }> = [];
    let currentClusterStart = -1;
    let lastCitationEnd = -1;

    // Group citations that are close to each other into clusters
    for (let i = 0; i < allCitations.length; i++) {
      const citation = allCitations[i];
      const citationStart = citation.index!;
      const citationEnd = citationStart + citation[0].length;

      // Check if this citation is adjacent to the previous one
      // Allow only whitespace between citations
      const textBetween = contentWithLinks.substring(
        lastCitationEnd,
        citationStart,
      );
      const isAdjacent = /^\s*$/.test(textBetween);

      if (isAdjacent && lastCitationEnd !== -1) {
        // Part of current cluster
        if (currentClusterStart === -1) {
          currentClusterStart = i - 1; // Include the previous citation
        }
      } else {
        // End the current cluster if there was one
        if (currentClusterStart !== -1) {
          citationClusters.push({
            start: currentClusterStart,
            end: i - 1,
          });
          currentClusterStart = -1;
        }
        // This citation might start a new cluster
      }

      lastCitationEnd = citationEnd;
    }

    // Add the last cluster if there is one
    if (currentClusterStart !== -1) {
      citationClusters.push({
        start: currentClusterStart,
        end: allCitations.length - 1,
      });
    }

    // Process each cluster to remove duplicate citations
    for (const cluster of citationClusters) {
      // Get all citations in this cluster
      const clusterCitations = allCitations.slice(
        cluster.start,
        cluster.end + 1,
      );

      // Track unique citation numbers in this cluster
      const uniqueCitationNumbers = new Set<string>();
      const citationsToRemove: number[] = [];

      // Identify duplicate citations to remove
      for (let i = 0; i < clusterCitations.length; i++) {
        const citation = clusterCitations[i];
        const citationNumber = citation[1]; // Group 1 is the number

        if (uniqueCitationNumbers.has(citationNumber)) {
          // This is a duplicate, mark for removal
          citationsToRemove.push(cluster.start + i);
        } else {
          // First occurrence, add to set
          uniqueCitationNumbers.add(citationNumber);
        }
      }

      // Remove the duplicates starting from the end to avoid index shifting
      for (let i = citationsToRemove.length - 1; i >= 0; i--) {
        const indexToRemove = citationsToRemove[i];
        const citation = allCitations[indexToRemove];
        const start = citation.index!;
        const end = start + citation[0].length;

        // Remove the citation and any whitespace following it up to the next citation
        let removeTo = end;
        if (indexToRemove < allCitations.length - 1) {
          const nextCitation = allCitations[indexToRemove + 1];
          const nextStart = nextCitation.index!;
          // Only remove whitespace, not other text
          const textBetween = contentWithLinks.substring(end, nextStart);
          if (/^\s*$/.test(textBetween)) {
            removeTo = nextStart;
          }
        }

        contentWithLinks =
          contentWithLinks.substring(0, start) +
          contentWithLinks.substring(removeTo);

        // Update indices of all following citations
        const shift = removeTo - start;
        for (let j = indexToRemove + 1; j < allCitations.length; j++) {
          if (allCitations[j].index !== undefined) {
            allCitations[j].index! -= shift;
          }
        }
      }
    }

    // Build a citation map for the references section
    interface CitationInfo {
      number: number;
      sourceId: string;
    }

    const citations: Record<string, CitationInfo> = {};

    // Use the sourceIdToNumber map to build the citations map
    Object.entries(sourceIdToNumber).forEach(([sourceId, number]) => {
      citations[sourceId] = {
        number,
        sourceId,
      };
    });

    // Add streaming marker if needed
    if (isStreaming) {
      contentWithLinks += ' [](streaming.example.com)';
    }

    // Log the number of chunks without a corresponding server side text chunk
    const missingTextChunkIds = chunkIds.filter(
      (chunkId) =>
        !textChunksQuery.data?.some((chunk) => chunk.textChunkId === chunkId),
    );
    logger.warn(
      `Number of chunks without a corresponding server side text chunk: ${missingTextChunkIds.length}`,
    );

    // Log the number of sources without a corresponding server side source
    const missingSourceIds = sourceIds.filter(
      (sourceId) =>
        !sourcesQuery.data?.some((source) => source.sourceId === sourceId),
    );
    logger.warn(
      `Number of sources without a corresponding server side source: ${missingSourceIds.length}`,
    );

    // Lastly clean up any square bracket instances that were not replaced with links
    // This handles cases where the content contains square brackets that are not part of a link
    // We want to replace these with an empty string
    contentWithLinks = contentWithLinks.replace(/\[([^\]]+)\](?!\()/g, '');

    return {
      processedContent: contentWithLinks,
      citationMap: citations,
    };
  }, [
    content,
    sourceIds,
    isStreaming,
    chunkIds,
    textChunkIdToSourceId,
    basePath,
    textChunksQuery.data,
    sourcesQuery.data,
  ]);

  return (
    <MarkdownContainer>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node: _, ...props }) => (
            <p
              {...props}
              style={{ marginTop: '0.25rem', marginBottom: '0.25rem' }}
            />
          ),
          a: ({ node: _, ...props }) => {
            // Render a hover tooltip if the we are linking to a source
            if (props.href === 'streaming.example.com') {
              return streamingStatus ? (
                <StyledStreamingTag color={token.colorPrimary} bordered={false}>
                  <Spin indicator={<LoadingOutlined spin />} size="small" />
                  <StreamingText>{streamingStatus}</StreamingText>
                </StyledStreamingTag>
              ) : (
                <StreamingDot />
              );
            } else if (props.href?.startsWith(`${basePath}/library/sources/`)) {
              const sourceId = props.href.split('/').pop();

              // Check if this is a placeholder source during streaming
              if (isStreaming) {
                // Safely check the content of children
                const childText = React.Children.toArray(props.children)
                  .map((child) => (typeof child === 'string' ? child : ''))
                  .join('');

                if (childText.startsWith('stream_')) {
                  // Extract the citation number or use a loading placeholder
                  const citationNumber = childText.replace('stream_', '');

                  return (
                    <Tooltip title="Citation source is loading...">
                      <a href={`#`}>
                        <IEEECitation>
                          [{citationNumber === 'loading' ? '' : citationNumber}]
                        </IEEECitation>
                      </a>
                    </Tooltip>
                  );
                }
              }

              const source = sourcesQuery.data?.find(
                (source) => source.sourceId === sourceId,
              );
              if (source) {
                const citationNum = React.Children.toArray(props.children)[0];

                // Handle clicks on citations to expand the references section
                const handleCitationClick = (e: React.MouseEvent) => {
                  e.preventDefault(); // Prevent default anchor behavior

                  // Get the target reference ID
                  const targetId = `ref-${citationNum}`;

                  // Expand the references section first
                  setReferencesExpanded(true);

                  // Wait for the DOM to update after expansion (150ms should be enough)
                  setTimeout(() => {
                    // Find the target element and scroll to it
                    const targetElement = document.getElementById(targetId);
                    if (targetElement) {
                      targetElement.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 150);
                };

                return (
                  <Tooltip
                    title={
                      <div
                        style={{ color: token.colorText, fontSize: '0.65rem' }}
                      >
                        <b>{source.title}</b>
                        <p>{source.description}</p>
                        {source.url && (
                          <p>
                            <UrlContainer>
                              <SourceLink
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {extractDomain(source.url)}
                                <ExternalLinkIcon />
                              </SourceLink>
                            </UrlContainer>
                          </p>
                        )}
                      </div>
                    }
                    mouseEnterDelay={0.2}
                    color={token.colorBgBase}
                  >
                    <a
                      href={`#ref-${citationNum}`}
                      onClick={handleCitationClick}
                    >
                      <IEEECitation>[{citationNum}]</IEEECitation>
                    </a>
                  </Tooltip>
                );
              }
            }
            return <a {...props} />;
          },
          h1: ({ node: _, ...props }) => (
            <Typography.Title
              level={2}
              style={{ marginTop: '0.25rem', fontSize: '1rem' }}
              {...props}
            />
          ),
          h2: ({ node: _, ...props }) => (
            <Typography.Title
              level={3}
              style={{ marginTop: '1rem', fontSize: '0.9rem' }}
              {...props}
            />
          ),
          h3: ({ node: _, ...props }) => (
            <Typography.Title
              level={4}
              style={{ marginTop: '1rem', fontSize: '0.85rem' }}
              {...props}
            />
          ),
          h4: ({ node: _, ...props }) => (
            <Typography.Title
              level={5}
              style={{ marginTop: '1rem', fontSize: '0.8rem' }}
              {...props}
            />
          ),
          // Add table components
          table: ({ node: _, ...props }) => <StyledTable {...props} />,
          thead: ({ node: _, ...props }) => <StyledTableHead {...props} />,
          tbody: ({ node: _, ...props }) => <StyledTableBody {...props} />,
          tr: ({ node: _, ...props }) => <StyledTableRow {...props} />,
          th: ({ node: _, ...props }) => <StyledTableHeaderCell {...props} />,
          td: ({ node: _, ...props }) => <StyledTableCell {...props} />,
        }}
      >
        {processedContent}
      </ReactMarkdown>

      {/* References Section - Only show for assistant messages */}
      {showReferences && (
        <ReferencesSection>
          <StyledCollapse
            ghost
            className={
              Object.keys(citationMap).length === 0 ? 'empty-references' : ''
            }
            expandIcon={({ isActive }) => (
              <CaretRightOutlined
                rotate={isActive ? 90 : 0}
                style={{ fontSize: '0.75rem' }}
              />
            )}
            activeKey={referencesExpanded ? ['1'] : []}
            onChange={(key) => setReferencesExpanded(key.includes('1'))}
            items={[
              {
                key: '1',
                label: 'References',
                children: (
                  <ReferencesList>
                    {Object.keys(citationMap).length > 0 ? (
                      // Sort by citation number to ensure sequential display
                      Object.entries(citationMap)
                        .sort(([_, a], [__, b]) => a.number - b.number)
                        .map(([sourceId, citation]) => {
                          const source = sourcesQuery.data?.find(
                            (src) => src.sourceId === sourceId,
                          );

                          if (!source) return null;

                          return (
                            <ReferenceItem
                              key={`ref-${citation.number}`}
                              id={`ref-${citation.number}`}
                            >
                              <ReferenceNumber>
                                [{citation.number}]
                              </ReferenceNumber>
                              {source.title}{' '}
                              {source.description && `${source.description}.`}{' '}
                              {source.url && (
                                <>
                                  [Online]. Available:{' '}
                                  <UrlContainer>
                                    <SourceLink
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {extractDomain(source.url)}
                                      <ExternalLinkIcon />
                                    </SourceLink>
                                  </UrlContainer>
                                </>
                              )}
                            </ReferenceItem>
                          );
                        })
                    ) : (
                      <EmptyReferenceItem>
                        No references available
                      </EmptyReferenceItem>
                    )}
                  </ReferencesList>
                ),
              },
            ]}
          />
        </ReferencesSection>
      )}
    </MarkdownContainer>
  );
};

export default MarkdownContent;
